import type { FunctionComponent, MouseEvent } from 'react';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { useMemberId } from '@trello/authentication';
import { useOrganizationMembershipType } from '@trello/business-logic-react/organization';
import { intl } from '@trello/i18n';
import { useBoardId, useWorkspaceId } from '@trello/id-context';
import type { Tag } from '@trello/model-types';
import { Button } from '@trello/nachos/button';
import { CheckIcon } from '@trello/nachos/icons/check';
import { EditIcon } from '@trello/nachos/icons/edit';
import {
  Popover,
  PopoverPlacement,
  PopoverScreen,
  usePopover,
} from '@trello/nachos/popover';
import {
  PopoverMenu,
  PopoverMenuButton,
  PopoverMenuItemSeparator,
} from '@trello/nachos/popover-menu';
import type { TeamTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { BoardListItemTagAdd } from 'app/src/components/BoardListItemTagAdd';
import { useBoardTagsFragment } from './BoardTagsFragment.generated';
import { BoardTagsDocument } from './BoardTagsQuery.generated';
import { CreateNewCollection } from './CreateNewCollection';
import { EditCollection } from './EditCollection';
import { EmptyState } from './EmptyState';
import { useOrgTagsFragment } from './OrgTagsFragment.generated';
import { useToggleCollectionMutation } from './ToggleCollectionMutation.generated';

import styles from './EditableCollectionsSelect.less';

const Screen = {
  Empty: -1,
  SelectTag: 0,
  CreateTag: 1,
  EditTag: 2,
} as const;

export const screenAnalyticsNames: {
  [key: number]:
    | 'addTagInlineDialog'
    | 'editTagInlineDialog'
    | 'selectTagInlineDialog';
} = {
  [Screen.SelectTag]: 'selectTagInlineDialog',
  [Screen.CreateTag]: 'addTagInlineDialog',
  [Screen.EditTag]: 'editTagInlineDialog',
};

export const EditableCollectionsSelect: FunctionComponent = () => {
  const memberId = useMemberId();
  const workspaceId = useWorkspaceId()!;
  const boardId = useBoardId()!;

  const { data: organization } = useOrgTagsFragment({
    from: { id: workspaceId },
  });
  const { data: board } = useBoardTagsFragment({
    from: { id: boardId },
  });

  const { triggerRef, toggle, popoverProps, push } = usePopover({
    placement: PopoverPlacement.RIGHT_START,
    initialScreen: organization?.tags?.length ? Screen.SelectTag : Screen.Empty,
  });

  const membershipType = useOrganizationMembershipType({
    idMember: memberId,
    idOrganization: workspaceId,
  });

  const isMemberOfOrg = ['admin', 'normal'].includes(membershipType as string);
  const isAdminOfOrg = membershipType === 'admin';

  const [idTagEditing, setIdTagEditing] = useState<string>('');
  const goToCreateScreen = useCallback(() => push(Screen.CreateTag), [push]);
  const goToSelectScreen = useCallback(() => push(Screen.SelectTag), [push]);
  const goToEditScreen = useCallback(
    (idTag: string) => (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIdTagEditing(idTag);
      push(Screen.EditTag);
    },
    [push, setIdTagEditing],
  );

  const [toggleTag] = useToggleCollectionMutation();
  const onToggleTag = useCallback(
    (idTag: string) => async () => {
      const isEnabled = (board?.idTags ?? []).includes(idTag);
      const traceId = Analytics.startTask({
        taskName: 'edit-board/idTags',
        source: 'workspaceBoardsScreen',
      });
      try {
        await toggleTag({
          variables: {
            idTag,
            idBoard: boardId,
            toggleOff: isEnabled,
            traceId,
          },
          refetchQueries: [
            {
              query: BoardTagsDocument,
              variables: { boardId },
              context: {
                operationName: 'ToggleCollectionMutation',
                document: BoardTagsDocument,
              },
            },
          ],
        });
        Analytics.sendTrackEvent({
          action: 'toggled',
          actionSubject: 'collection',
          source: 'workspaceBoardsScreen',
          attributes: {
            taskId: traceId,
            idCollection: idTag,
            toggleOff: isEnabled,
          },
          containers: {
            board: { id: boardId },
            workspace: { id: workspaceId },
          },
        });
        Analytics.taskSucceeded({
          taskName: 'edit-board/idTags',
          source: 'workspaceBoardsScreen',
          traceId,
        });
      } catch (err) {
        Analytics.taskFailed({
          taskName: 'edit-board/idTags',
          source: 'workspaceBoardsScreen',
          error: err,
          traceId,
        });
      }
    },
    [board?.idTags, boardId, toggleTag, workspaceId],
  );

  return (
    <>
      <BoardListItemTagAdd canAddTag onClick={toggle} triggerRef={triggerRef} />
      <Popover {...popoverProps}>
        <PopoverScreen
          id={Screen.Empty}
          title={intl.formatMessage({
            id: 'view title.collections',
            defaultMessage: 'Collections',
            description: 'Collections popover title',
          })}
        >
          <EmptyState
            collectionsEnabled
            onCreateClick={goToCreateScreen}
            orgId={organization?.id ?? ''}
            orgName={organization?.name ?? ''}
            isMember={isMemberOfOrg}
            isAdmin={isAdminOfOrg}
          />
        </PopoverScreen>
        <PopoverScreen
          id={Screen.SelectTag}
          title={intl.formatMessage({
            id: 'view title.pick tags',
            defaultMessage: 'Change collection',
            description: 'Change collection',
          })}
        >
          <PopoverMenu testId={getTestId<TeamTestIds>('collection-list')}>
            {organization?.tags?.map((tag: Tag) => (
              <PopoverMenuButton
                key={tag.id}
                className={styles.tagItem}
                testId={getTestId<TeamTestIds>('collection-button')}
                onClick={onToggleTag(tag.id)}
                iconBefore={
                  (board?.idTags ?? []).includes(tag.id) ? (
                    <CheckIcon size="small" />
                  ) : (
                    <div className={styles.iconSpacer} />
                  )
                }
                iconAfter={
                  <Button
                    appearance="subtle"
                    className={styles.editButton}
                    testId={getTestId<TeamTestIds>('collection-edit-button')}
                    onClick={goToEditScreen(tag.id)}
                    title={`Rename ${tag.name}`}
                  >
                    <EditIcon label={`Rename ${tag.name}`} size="small" />
                  </Button>
                }
              >
                {tag.name}
              </PopoverMenuButton>
            ))}
            <PopoverMenuItemSeparator />
          </PopoverMenu>

          <Button
            shouldFitContainer
            data-testid={getTestId<TeamTestIds>('create-collection-button')}
            onClick={goToCreateScreen}
          >
            <FormattedMessage
              id="templates.org_filtering.create-tag"
              defaultMessage="Create a collection…"
              description="Create a collection…"
            />
          </Button>
        </PopoverScreen>
        <PopoverScreen
          id={Screen.CreateTag}
          title={intl.formatMessage({
            id: 'view title.add tag',
            description: 'Create a new collection',
            defaultMessage: 'Create new collection',
          })}
        >
          <CreateNewCollection
            orgId={workspaceId}
            onCreate={goToSelectScreen}
          />
        </PopoverScreen>
        <PopoverScreen
          id={Screen.EditTag}
          title={intl.formatMessage({
            id: 'view title.edit tag',
            description: 'Edit collection',
            defaultMessage: 'Edit collection',
          })}
        >
          <EditCollection tagId={idTagEditing} onComplete={goToSelectScreen} />
        </PopoverScreen>
      </Popover>
    </>
  );
};
